import React, { useState, useRef } from "react";
import BooksTable from "../tables/BooksTable";
import TracksByBookTable from "../tables/TracksByBookTable";
import { successMsg } from "../SuccessHandler";
import Swal from "sweetalert2";
import CopyBookModal from "./CopyBookModal";
import LastChangesModal from "../lastChanges/LastChangesModal";
import ErrorCodes from "../../constants/ErrorCodes";
import SiteConfig from "../../constants/SiteConfig";
import { useInsertFunctions } from "../../functions";
import Rights from "../../constants/Rights";

const TrackByBookModal = ({
  data,
  books,
  gameProviders,
  setSelectedProvider,
  selectedProvider,
  getTracksByBooks,
  idFirstBook,
  tracksByBooks,
  setTracksByBooks,
  getBooks,
  setRefresh,
  bookOptionSelected,
  setBookOptionSelected,
  lastChanges,
  setLastChanges,
  lastChangesInfo,
  setLastChangesInfo,
  fieldChangesInfo,
  setFieldChangesInfo,
  getFieldChanges,
  openLastChanges,
  bookName,
  setBookName,
  setSessionNoLongerExists,
  publicIP,
  setOpenByBooksModal,
  getTracksListCbx,
  tracksListCbx,
}) => {
  const [filterInput, setFilterInput] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedRow, setSelectedRow] = useState(0);
  const closeButtonRef = useRef();
  const divRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { insertTracksByBook } = useInsertFunctions();
  const userRightsList = JSON.parse(localStorage.getItem("UserRights")) || [];
  const [isCopyOpen, setIsCopyOpen] = useState(false);

  const tracksByBookChangeHandler = (trackID) => {
    setTracksByBooks((prevTracksByBooks) => {
      const trackIndex = prevTracksByBooks.findIndex(
        (track) => track.TrackID === trackID,
      );

      if (trackIndex !== -1) {
        // The trackID already exists and must be deleted.
        return prevTracksByBooks.filter((track) => track.TrackID !== trackID);
      } else {
        // The trackID does not exist, it must be added.
        return [
          ...prevTracksByBooks,
          {
            TrackID: trackID,
          },
        ];
      }
    });
    setHasChanges(true);
  };

  //Insert Tracks By Books Function
  const handleInsertTracksByBook = async (tracksByBook, close) => {
    if (hasChanges) {
      const res = await insertTracksByBook(
        {
          IDBook: idFirstBook,
          LastModification: new Date().toISOString(),
          TrackByBookList: tracksByBook,
          PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
          IP: publicIP,
        },
        data.LogonSessionID,
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        successMsg("Save Tracks By Book");
        setHasChanges(false);
        setTracksByBooks(tracksByBook);
        if (close) {
          closeButtonRef.current.click();
          setTracksByBooks([]);
          setSelectedRow(0);
          setBookOptionSelected("");
          setOpenByBooksModal(false);
        }
      }
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const closeModal = () => {
    if (hasChanges) {
      Swal.fire({
        title: "Are you sure?",
        text: "Changes will be saved!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Exit anyway",
        confirmButtonText: "Yes, save it!",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.value) {
          handleInsertTracksByBook(tracksByBooks, true);
          setRefresh(false);
          setSelectedProvider(-1);
          setFilterInput("");
          setSelectAll(false);
        } else {
          closeButtonRef.current.click();
          const div = divRef.current;
          if (div) {
            div.scrollTop = 0;
          }
          setHasChanges(false);
          setTracksByBooks([]);
          setSelectedRow(0);
          setBookOptionSelected("");
          setRefresh(false);
          setSelectedProvider(-1);
          setFilterInput("");
          setSelectAll(false);
          setOpenByBooksModal(false);
        }
      });
    } else {
      closeButtonRef.current.click();
      const div = divRef.current;
      if (div) {
        div.scrollTop = 0;
      }
      setHasChanges(false);
      setTracksByBooks([]);
      setSelectedRow(0);
      setBookOptionSelected("");
      setRefresh(false);
      setSelectedProvider(-1);
      setFilterInput("");
      setSelectAll(false);
      setOpenByBooksModal(false);
    }
  };

  // This function is executed when user clicks on Refresh button
  const refreshTrackByBooks = () => {
    getBooks();
    getTracksByBooks(bookOptionSelected);
    setRefresh(true);
  };

  return (
    <>
      <div
        className="modal fade"
        id="trackByBookModal"
        role="dialog"
        aria-hidden="false"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5
                className="modal-title"
                id="trackByBookModal"
                style={{ cursor: "default" }}
              >
                Tracks by Book
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-4">
                  <BooksTable
                    books={books}
                    getTracksByBooks={getTracksByBooks}
                    filterInput={filterInput}
                    setFilterInput={setFilterInput}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                    handleInsertTracksByBook={handleInsertTracksByBook}
                    tracksByBooks={tracksByBooks}
                    setBookOptionSelected={setBookOptionSelected}
                    setBookName={setBookName}
                    setSelectAll={setSelectAll}
                    setTracksByBooks={setTracksByBooks}
                  />
                </div>
                <div className="col-sm-8">
                  <TracksByBookTable
                    gameProviders={gameProviders}
                    setSelectedProvider={setSelectedProvider}
                    selectedProvider={selectedProvider}
                    trackList={tracksListCbx}
                    tracksByBooks={tracksByBooks}
                    tracksByBookChangeHandler={tracksByBookChangeHandler}
                    setTracksByBooks={setTracksByBooks}
                    setHasChanges={setHasChanges}
                    selectAll={selectAll}
                    setSelectAll={setSelectAll}
                    getTracksListCbx={getTracksListCbx}
                    selectedRow={selectedRow}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="col-sm-8">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => refreshTrackByBooks()}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setIsCopyOpen(true)}
                >
                  Copy
                </button>

                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={() => {
                      openLastChanges(
                        "TRACKBYBOOKLOG",
                        bookOptionSelected,
                        0,
                        bookName,
                      );
                      setIsModalOpen(true);
                    }}
                  >
                    Last Changes
                  </button>
                )}
              </div>
              <div
                className="col-sm-4"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => handleInsertTracksByBook(tracksByBooks, false)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isCopyOpen && (
        <CopyBookModal
          isOpen={isCopyOpen}
          onClose={() => setIsCopyOpen(false)}
          data={data}
          books={books}
          bookOptionSelected={bookOptionSelected}
          setSessionNoLongerExists={setSessionNoLongerExists}
          publicIP={publicIP}
        />
      )}

      {isModalOpen && (
        <LastChangesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lastChanges={lastChanges}
          setLastChanges={setLastChanges}
          lastChangesInfo={lastChangesInfo}
          getFieldChanges={getFieldChanges}
          fieldChangesInfo={fieldChangesInfo}
          setLastChangesInfo={setLastChangesInfo}
          setFieldChangesInfo={setFieldChangesInfo}
        />
      )}
    </>
  );
};

export default TrackByBookModal;
